import { supabase } from "~/integrations/supabase/client";

import type {
  CreateProductTransferInput,
  CreateProductTransferResult,
  FgPalletTransferInput,
  FgPalletTransferResult,
  FgPlantTransferInput,
  FgPlantTransferResult,
  FgPlantTransferWithNames,
  InternalTransferGuideData,
  ProductLoteAvailability,
  ProductTransferActionResult,
  ProductTransferWithNames,
  ReadyPallet,
  UpdateProductTransferInput,
  WarehouseOption,
} from "~/types/productTransfer";

// Header + joined product/warehouse/performer names, newest first. Two FKs point
// at wms_warehouses, so each side is disambiguated by its constraint name.
const TRANSFER_SELECT = `
  *,
  product:products(id, short_name, sku, base_measurement_unit, unit_code),
  from_warehouse:wms_warehouses!product_transfers_from_warehouse_id_fkey(id, code, name),
  to_warehouse:wms_warehouses!product_transfers_to_warehouse_id_fkey(id, code, name),
  production_batch:production_batches(batch_number, lot_number),
  performed_by_profile:profiles!performed_by(id, name, lastname),
  received_by_profile:profiles!received_by(id, name, lastname)
`;

// Header + joined product/performer names for the FG-between-plants history.
const FG_TRANSFER_SELECT = `
  *,
  product:products(id, short_name, sku, base_measurement_unit, unit_code),
  performed_by_profile:profiles!performed_by(id, name, lastname)
`;

const lote = (t: ProductTransferWithNames) =>
  t.lot_number ??
  t.production_batch?.lot_number ??
  t.production_batch?.batch_number ??
  "Sin lote";

export const productTransferService = {
  /**
   * List all producto-terminado transfers (bodega B1 <-> B2 <-> NC), newest first.
   */
  async listTransfers(): Promise<ProductTransferWithNames[]> {
    const { data, error } = await supabase
      .from("product_transfers")
      .select(TRANSFER_SELECT)
      .order("created_at", { ascending: false });

    if (error) throw error;
    return (data ?? []) as unknown as ProductTransferWithNames[];
  },

  /**
   * Transfers still in transit (sent, pending the receiving bodega's confirmation).
   */
  async listPendingReceipts(): Promise<ProductTransferWithNames[]> {
    const { data, error } = await supabase
      .from("product_transfers")
      .select(TRANSFER_SELECT)
      .eq("status", "in_transit")
      .order("created_at", { ascending: false });

    if (error) throw error;
    return (data ?? []) as unknown as ProductTransferWithNames[];
  },

  /**
   * List all finished-good between-plants transfers (P1 <-> P2 <-> NC, TF-#####),
   * newest first. These relocate un-ingested produced stock and are recorded in
   * fg_plant_transfers — a separate table from the bodega product_transfers, with
   * no reception flow (they complete instantly). Table isn't in the generated
   * types yet, hence the `as never` on the source.
   */
  async listFgPlantTransfers(): Promise<FgPlantTransferWithNames[]> {
    const { data, error } = await supabase
      .from("fg_plant_transfers" as never)
      .select(FG_TRANSFER_SELECT)
      .order("created_at", { ascending: false });

    if (error) throw error;
    return (data ?? []) as unknown as FgPlantTransferWithNames[];
  },

  /**
   * Confirm reception of an in-transit transfer: deposits the stock into the
   * destination bodega. Idempotent (a second CHECK is a no-op).
   */
  async receiveTransfer(
    transferId: string,
    performedBy: string
  ): Promise<ProductTransferActionResult> {
    const { data, error } = await supabase.rpc(
      "receive_product_transfer" as never,
      { p_transfer_id: transferId, p_received_by: performedBy } as never
    );

    if (error) throw error;
    return data as unknown as ProductTransferActionResult;
  },

  /**
   * Cancel an in-transit transfer: returns the stock to the origin bodega.
   */
  async cancelTransfer(
    transferId: string,
    performedBy: string
  ): Promise<ProductTransferActionResult> {
    const { data, error } = await supabase.rpc(
      "cancel_product_transfer" as never,
      { p_transfer_id: transferId, p_cancelled_by: performedBy } as never
    );

    if (error) throw error;
    return data as unknown as ProductTransferActionResult;
  },

  /**
   * Edit an in-transit transfer's quantity + notes (creator/admin only, enforced
   * by the RPC). Adjusts the origin box delta and products.stock; the guía can be
   * reprinted with the new quantity before the destination confirms reception.
   */
  async updateTransfer(
    input: UpdateProductTransferInput
  ): Promise<CreateProductTransferResult> {
    const { data, error } = await supabase.rpc(
      "update_product_transfer" as never,
      {
        p_transfer_id: input.transferId,
        p_new_boxes: input.boxes,
        p_notes: input.notes ?? null,
        p_performed_by: input.performedBy,
      } as never
    );

    if (error) throw error;
    return data as unknown as CreateProductTransferResult;
  },

  /**
   * Data for the printable "Guía de Traslado Interno".
   */
  async getProductTransferForDocument(
    transferId: string
  ): Promise<InternalTransferGuideData> {
    const { data, error } = await supabase
      .from("product_transfers")
      .select(TRANSFER_SELECT)
      .eq("id", transferId)
      .single();

    if (error) throw error;
    const t = data as unknown as ProductTransferWithNames;
    const performer = t.performed_by_profile;
    return {
      transferNumber: t.transfer_number,
      transferredAt: t.transferred_at,
      fromLabel: t.from_warehouse
        ? `${t.from_warehouse.name} (${t.from_warehouse.code})`
        : "—",
      toLabel: t.to_warehouse
        ? `${t.to_warehouse.name} (${t.to_warehouse.code})`
        : "—",
      performedBy: performer
        ? `${performer.name ?? ""} ${performer.lastname ?? ""}`.trim() || "—"
        : "—",
      notes: t.notes,
      lines: [
        {
          codigo: t.product?.sku ?? "—",
          item: t.product?.short_name ?? "Producto",
          um: t.product?.base_measurement_unit ?? t.product?.unit_code ?? "CAJA",
          lote: lote(t),
          cantidad: t.boxes,
        },
      ],
    };
  },

  /**
   * Active bodegas for the origin/destination selects.
   */
  async listWarehouses(): Promise<WarehouseOption[]> {
    const { data, error } = await supabase
      .from("wms_warehouses")
      .select("id, code, name")
      .eq("is_active", true)
      .order("code");

    if (error) throw error;
    return (data ?? []) as WarehouseOption[];
  },

  /**
   * The product's unit of measure (CAJA/UNI/LTS/KGS/…) so the transfer form can
   * label quantities correctly instead of always saying "cajas". The WMS count
   * is already stored in this unit; nothing is converted.
   */
  async getProductUnit(productId: string): Promise<string | null> {
    const { data, error } = await supabase
      .from("products")
      .select("base_measurement_unit, unit_code")
      .eq("id", productId)
      .single();

    if (error) throw error;
    return data?.base_measurement_unit ?? data?.unit_code ?? null;
  },

  /**
   * Available lotes of a product per bodega (with unreserved box counts).
   */
  async getLotes(productId: string): Promise<ProductLoteAvailability[]> {
    const { data, error } = await supabase.rpc(
      "get_product_lotes_for_transfer",
      { p_product_id: productId }
    );

    if (error) throw error;
    return (data ?? []) as ProductLoteAvailability[];
  },

  /**
   * Send a PT transfer. The RPC atomically decrements the source bodega and puts
   * the stock in transit (status='in_transit'); it lands in the destination only
   * once the receiving bodega confirms via receiveTransfer.
   */
  async createTransfer(
    params: CreateProductTransferInput
  ): Promise<CreateProductTransferResult> {
    const { data, error } = await supabase.rpc("create_product_transfer", {
      p_from_warehouse: params.fromWarehouse,
      p_to_warehouse: params.toWarehouse,
      p_product_id: params.productId,
      p_boxes: params.boxes,
      p_lot_number: params.lotNumber,
      p_production_batch_id: params.productionBatchId,
      p_notes: params.notes ?? null,
      p_performed_by: params.performedBy,
    });

    if (error) throw error;
    return data as unknown as CreateProductTransferResult;
  },

  /**
   * Move a batch's un-ingested (pending) finished-good stock between plants
   * (P1 <-> P2 <-> NC). Relocates pending-ingestion quantity only: no wms_inventory,
   * net-zero on products.stock. Used when the transfer origin is a plant.
   */
  async transferFgBetweenPlants(
    params: FgPlantTransferInput
  ): Promise<FgPlantTransferResult> {
    const { data, error } = await supabase.rpc(
      "transfer_fg_between_plants" as never,
      {
        p_production_batch_id: params.productionBatchId,
        p_from_plant: params.fromPlant,
        p_to_plant: params.toPlant,
        p_boxes: params.boxes,
        p_notes: params.notes ?? null,
        p_performed_by: params.performedBy,
      } as never
    );

    if (error) throw error;
    return data as unknown as FgPlantTransferResult;
  },

  /**
   * Finished ("Listo") un-ingested pallets of a still-open batch at a holding plant,
   * feeding the transfer dialog's pallet checklist.
   */
  async getBatchReadyPallets(
    batchId: string,
    plant: string
  ): Promise<ReadyPallet[]> {
    const { data, error } = await supabase.rpc(
      "get_batch_ready_pallets" as never,
      { p_batch_id: batchId, p_plant: plant } as never
    );

    if (error) throw error;
    return (data ?? []) as unknown as ReadyPallet[];
  },

  /**
   * Move whole finished PALLETS of a still-open production batch between plants
   * (P1 <-> P2 <-> NC) — the partial-during-production traslado. Flips each pallet's
   * holding plant; no wms_inventory, net-zero on products.stock.
   */
  async transferFgPalletsBetweenPlants(
    params: FgPalletTransferInput
  ): Promise<FgPalletTransferResult> {
    const { data, error } = await supabase.rpc(
      "transfer_fg_pallets_between_plants" as never,
      {
        p_production_batch_id: params.productionBatchId,
        p_from_plant: params.fromPlant,
        p_to_plant: params.toPlant,
        p_pallet_ids: params.palletIds,
        p_notes: params.notes ?? null,
        p_performed_by: params.performedBy,
      } as never
    );

    if (error) throw error;
    return data as unknown as FgPalletTransferResult;
  },
};
